import {Inject, Injectable, PLATFORM_ID, TemplateRef} from '@angular/core';
import {GlobalMessageService, GlobalMessageType} from '@spartacus/core';
import {Store} from '@ngrx/store';
import {StateWithGlobalMessage} from '@spartacus/core/src/global-message/store/global-message-state';
import {IDcpGlobalErrorMessage} from '@app/core/models/interfaces/error';
import {DcpLoggingService} from '@dcp/configurator';
import {BehaviorSubject, Observable, ReplaySubject} from 'rxjs';
import {NavigationEnd, Router} from '@angular/router';
import {isPlatformBrowser} from '@angular/common';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {map} from 'rxjs/operators';

interface InternalError {
  errorType: string;
  name?: string;
  message: string;
  severity: string;
}

interface MessageObject {
  message?: string;
  errorMessage?: string;
  type?: GlobalMessageType;
  errorCode?: string;
  skipLogging?: boolean;
}

type DisplayLocation = 'errorGlobal' | 'errorLogging' | 'errorPage';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})

export class DcpGlobalMessageService extends GlobalMessageService {

  private innerCount: number = 0;
  private currentMessages: IDcpGlobalErrorMessage[] = [];
  private currentMessageSubject: BehaviorSubject<IDcpGlobalErrorMessage[]> = new BehaviorSubject<IDcpGlobalErrorMessage[]>(this.currentMessages);
  private currentMessageTemplateSubject: BehaviorSubject<TemplateRef<any>> = new BehaviorSubject<TemplateRef<any>>(undefined);
  private hiddenGroups = new Set<string>();
  private messagesAvailable$ = new ReplaySubject<boolean>(1);
  private currentMessageTemplate: TemplateRef<any>;

  constructor(
    @Inject(PLATFORM_ID) public platformId: string,
    public router: Router,
    private dcpLoggingService: DcpLoggingService,
    store: Store<StateWithGlobalMessage>,
  ) {
    super(store);
    this.init();
  }

  public getMessagesDisplayed$(): Observable<boolean> {
    return this.messagesAvailable$.asObservable();
  }

  init(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.router.events.pipe(untilDestroyed(this)).subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.resetMessages();
        }
      });
    }
  }

  resetMessages(): void {
    this.currentMessages = [];
    this.currentMessageSubject.next(this.currentMessages);
    this.innerCount = 0;
    this.updateMessagesAvailable();
  }

  private createInternalErrorObject(message: string, type?: string, errorCode?: string): InternalError {
    const severityMapping: Record<string, string> = {
      '[GlobalMessage] Error': 'error',
      '[GlobalMessage] Warning': 'warning',
    };

    const severity = severityMapping[type] || 'info';
    const errorType = type || 'BACKEND';

    return {
      errorType,
      name: errorCode,
      message,
      severity,
    };
  }

  private logErrorGlobal(message: string, location: any, type?: string, errorCode?: string): void {
    const internalError = this.createInternalErrorObject(message, type, errorCode);
    this.dcpLoggingService.error(internalError, location);
  }

  private logErrorLogging(message: string, location: any): void {
    this.dcpLoggingService.error(message, location);
  }

  private logErrorPage(): void {
    // Implement logic for error page
  }

  private publishEventMapping(message: string, location: any, displayLocation: DisplayLocation, type?: string, errorCode?: string): void {
    switch (displayLocation) {
      case 'errorGlobal':
        this.logErrorGlobal(message, location, type, errorCode);
        break;
      case 'errorLogging':
        this.logErrorLogging(message, location);
        break;
      case 'errorPage':
        this.logErrorPage();
        break;
    }
  }

  private addMessageMapping(messageObject, location: any, displayLocation: DisplayLocation): void {
    switch (displayLocation) {
      case 'errorGlobal':
        if (!messageObject.id) {
          messageObject.id = this.innerCount.toString();
          this.innerCount++;
        }

        const errorMessage = messageObject.message || messageObject.errorMessage;
        const type = messageObject.type;
        const errorCode = messageObject.errorCode;

        if (!messageObject.skipLogging) {
          this.publishEvent(errorMessage, location, 'errorGlobal', type, errorCode);
        }

        this.currentMessages.push(messageObject);
        this.updateSubject();
        this.scrollToComponent();
        break;
      case 'errorLogging':
        this.publishEvent(messageObject, location, 'errorLogging');
        break;
      case 'errorPage':
        // Implement logic for error page
        break;
    }
  }

  publishEvent(message: string, location: any, displayLocation: DisplayLocation, type?: string, errorCode?: string): void {
    this.publishEventMapping(message, location, displayLocation, type, errorCode);
  }

  addMessage(messageObject: MessageObject | any, location: any, displayLocation: DisplayLocation): void {
    this.addMessageMapping(messageObject, location, displayLocation);
  }

  toggleMessageGroupVisibility(group: string, hide: boolean) {
    if (hide) {
      this.hideMessageGroup(group);
    } else {
      this.showMessageGroup(group);
    }
  }

  showMessageGroup(groupCode: string) {
    this.hiddenGroups.delete(groupCode);
    this.updateSubject();
  }

  hideMessageGroup(groupCode: string) {
    this.hiddenGroups.add(groupCode);
    this.updateSubject();
  }

  filterOutHiddenMessages(messages: IDcpGlobalErrorMessage[]): IDcpGlobalErrorMessage[] {
    return messages.filter((msg) => !this.hiddenGroups.has(msg.group));
  }

  removeMessage(id: string, group?: string): void {
    this.currentMessages = this.currentMessages.filter(message => message.id !== id);
    if (group) {
      this.currentMessages = this.currentMessages.filter(message => message.group !== group);
    }
    this.updateSubject();
  }

  getActiveMessages(): Observable<IDcpGlobalErrorMessage[]> {
    return this.currentMessageSubject.asObservable().pipe(
      map((messages) => this.filterOutHiddenMessages(messages)),
    );
  }

  getActiveTemplate(): Observable<TemplateRef<any>> {
    return this.currentMessageTemplateSubject.asObservable();
  }

  setActiveTemplate(templateRef: TemplateRef<any>) {
    this.currentMessageTemplate = templateRef;
    this.currentMessageTemplateSubject.next(templateRef);
    this.updateMessagesAvailable();
  }

  updateMessagesAvailable() {
    this.messagesAvailable$.next(!!(this.currentMessageTemplate || this.currentMessages?.length));
  }

  scrollToComponent() {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        const element = document.getElementsByTagName('dcp-disposable-message')?.item(0);

        element?.scrollIntoView({
          block: 'center',
          inline: 'center'
        });
      });
    }
  }

  private updateSubject(): void {
    this.currentMessageSubject.next(this.currentMessages);
    this.updateMessagesAvailable();
  }
}
